/**
 * IndividualAmlInsightSection — "Identity Verification (Fraud Screening)"
 * card (LexisNexis-backed, one-button pull).
 *
 * LOCKED card design: docs/operations/person-modal-inventory/
 * 06-INDIVIDUAL-SURFACE-CLEANUP-MOCKUP.html (v6, Bill 2026-06-11).
 * Retitle + terse "Run" button + no at-rest prose: smoke test #2
 * (Bill 2026-06-12) — the FraudPoint band/procedure guidance renders only
 * in the post-run result area.
 *
 * One primary action — "Run". The server route
 * (/v3/identity/verify → VerifyDispatcher, BACKLOG-0518) is API-first:
 * the direct LexisNexis DDP connection (DdpApiAdapter, SourceMode
 * 'ddp-api') runs when configured, and the website pull (SourceMode
 * 'html-import') is the automatic backup whenever the direct connection
 * is unavailable, down, or not yet configured. Until LexisNexis provides
 * the API key every run takes the website-pull path (Bill 2026-06-11):
 *
 *   button → /v3/identity/verify → autofill payload copied to clipboard →
 *   LexisNexis popup opens → FSR runs the report → the import step appears
 *   IN-FLOW → paste the exported HTML report → ~80 fields parse →
 *   FraudPoint Score + Verify clearances auto-populate. (The LN Bridge
 *   extension import is detected by the same poll, BACKLOG-0508.)
 *
 * Standing card surfaces: the button, FraudPoint Score / Report Date /
 * Run by, the band/tier indicator, and View-full-report once a report
 * exists. The paste-report panel, prefill copy grid, and autofill
 * copy grid lives INSIDE the flow step — they render only while a pull
 * is active or an inquiry is still awaiting its report, never at rest.
 *
 * FraudPoint Score is the canonical decisioning value (750+ proceed ·
 * 500–749 collect-rules · 300–499 decline); Red Flags are advisory.
 * Both paths persist the same IdentityProviderExtract row shape;
 * SourceMode distinguishes (adapter-pattern decision 2026-05-21).
 */

import { useEffect, useRef, useState } from 'react';
import type { RaoPerson } from '../../../types';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import type {
  ImportLexisNexisHtmlSummary,
  SubjectIdentity,
  VerifyInquiryOutcome,
} from '@retail-ops/api-client';
import { IdentityExtractDetailModal } from './IdentityExtractDetailModal';
import { isExistingCustomer } from '../shared/existingCustomer';
import { deriveLnContactClearance, type VerifyClearanceMap } from '../shared/verifyClearance';
import {
  deriveLnAxesFromPerson,
  resolveLnRule,
  type LnChannel,
} from '../shared/lnVerificationRules';
import { useLnVerificationRules } from '../shared/useLnVerificationRules';
import { useConfig } from '@/contexts/ConfigContext';
import {
  joinLnInputLabels,
  missingLnVerifyInputs,
  readLnVerifyInputSettings,
} from '../shared/lnVerifyInputs';
import { deriveFraudPointFollowUp, type FollowUpItem } from './fraudPointFollowUp';
import { fraudPointFloors, showVerificationRiskIndex } from '../shared/lnDisplaySettings';
import { SupervisorOverridePanel } from './SupervisorOverridePanel';

// LAUNCH HOST — admin-settable, because it has to change twice on a deadline.
//
// LNRS is consolidating every endpoint from lexisnexis.com onto
// lexisnexisrisk.com; the retiring URLs begin decommissioning in Q4 2026 and
// LNRS recommends moving before 2026-10-15 (Transition FAQs.pdf).
//
// But the PCB LN Bridge extension injects its "Retrieve PII Data" button only
// on hosts in its manifest match list. The deployed 2.1.6 matches the LEGACY
// host only, so launching the new one logs the FSR into a page the extension
// cannot see: no button, and autofill silently does nothing (Bill 2026-09-23).
// Extension 2.3.0 matches BOTH, so once it is on every pilot device the
// setting flips to the lexisnexisrisk host and nothing breaks at the cutover.
//
// Sequencing is Bill's: stay on the deprecated host until the extension update
// is confirmed populated, then flip. Because this is a SystemSetting, that flip
// is a one-line change in the admin panel rather than a three-app redeploy —
// which matters with a hard date attached.
const AML_INSIGHT_URL_SETTING = 'lexisnexis.amlInsightUrl';
// 2026-10-08: fleet on LN Bridge 2.5.x (matches both hosts); legacy host decommissioned 2026-10-15.
const LEXISNEXIS_AML_INSIGHT_URL_FALLBACK = 'https://amlinsight.lexisnexisrisk.com/';

/**
 * Actual wire shape of POST /v3/identity/verify (IdentityFunction.cs):
 * the direct-connection branch returns { sourceMode:'ddp-api', extractId }
 * with NO clipboardPayload/nextStep; the website-pull branch returns
 * { sourceMode:'html-import', clipboardPayload, nextStep } with NO
 * extractId. The api-client DTO types the html-import shape; this widens
 * it to cover both branches of the server-side VerifyDispatcher.
 */
type VerifyOutcomeWire = Omit<VerifyInquiryOutcome, 'clipboardPayload' | 'nextStep'> & {
  clipboardPayload?: VerifyInquiryOutcome['clipboardPayload'];
  nextStep?: VerifyInquiryOutcome['nextStep'];
  extractId?: string;
};

export interface IndividualAmlInsightSectionProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  isChanged: (field: keyof RaoPerson) => boolean;
  readOnly?: boolean;
  /**
   * Origination channel for the cart. 'In-Branch' is the FSR-driven flow
   * (today's ROP-teams default); 'Online' is the RCP-customer-portal flow.
   * Drives the LN Verification Rule lookup per BACKLOG-0501. When omitted,
   * defaults to 'In-Branch' since ROP-teams is the FSR tool.
   */
  cartChannel?: LnChannel;
  /**
   * Cart mode (Production | UAT-Live | Test-Live | Sandbox). Sent on the
   * verify call. DdpApiAdapter picks the LexisNexis credential pair from it —
   * Test-Live goes to the SANDBOX institution, everything else to production.
   * Previously this was never sent, so the server fell back to its Production
   * default and a TEST cart was billed against the production org.
   */
  cartMode?: string;
}

interface ScoreTier {
  label: string;
  tone: 'ok' | 'warn' | 'err';
  bg: string;
  fg: string;
  border: string;
  rule: string;
}

/**
 * FraudPoint Score banding — VERBATIM per the legacy procedure
 * (Online Applicants.pdf pp.2-3; transcribed in
 * docs/operations/person-modal-inventory/2026-06-03-smoke-test-findings.md §D2;
 * memory project_fraudpoint_gating_d2_procedure_20260603). The FraudPoint Score
 * is the CANONICAL decisioning value — NOT the InstantID red-flags / risk index.
 *
 * `state` lets us surface the correct Moderate collect-rule: out-of-STATE is
 * unambiguously out-of-market; in-state (MO) keeps the in-market rule but flags
 * that out-of-county MO is also out-of-market (precise county classification is
 * the admin-config/enforcement layer — this is the FSR-facing guidance).
 */
function classifyScore(score: number | undefined, state?: string): ScoreTier | null {
  if (score == null || Number.isNaN(score)) return null;
  if (score >= 750) {
    return {
      label: 'Low risk (750+)',
      tone: 'ok',
      bg: 'var(--ok-bg)',
      fg: 'var(--ok-text)',
      border: 'var(--ok-fill)',
      rule: 'Proceed if address, SSN, and DOB are verified. If a discrepancy, collect ONE of: government photo ID, address verification, or SSN card.',
    };
  }
  if (score >= 500) {
    const outOfState = !!state && state.toUpperCase() !== 'MO';
    const collect = outOfState
      ? 'Out-of-market / out-of-state: government photo ID REQUIRED + SSN card REQUIRED + address verification (not required if the photo-ID address matches the application OR LexisNexis verifies the address).'
      : 'In-market (Phelps / Greene + bordering counties): government photo ID REQUIRED + address verification (if the photo-ID address ≠ application AND not LexisNexis-verifiable) OR SSN card (if SSN differs). Out-of-county MO = use the out-of-market rule.';
    return {
      label: 'Moderate risk (500–749)',
      tone: 'warn',
      bg: 'var(--warn-bg)',
      fg: 'var(--warn-text)',
      border: 'var(--warn-line)',
      rule: `Follow-up REQUIRED before continuing. ${collect} RDA/ATM not auto-restricted (case-by-case); incoming B2B transfers stay restricted.`,
    };
  }
  return {
    label: 'High risk (300–499)',
    tone: 'err',
    bg: 'var(--err-bg)',
    fg: 'var(--err-text)',
    border: 'var(--err-fill)',
    rule: 'Do NOT open the account. Decline + send the adverse-action letter (include an identity-theft brochure ONLY if there is genuine suspicion the applicant is a victim).',
  };
}

export function IndividualAmlInsightSection(
  props: IndividualAmlInsightSectionProps,
): JSX.Element | null {
  const { person, onChange, isChanged, readOnly = false, cartChannel = 'In-Branch', cartMode } = props;
  const { user } = useAuth();
  const api = useApiClient();
  const { config } = useConfig();
  const lnSettings = config.systemSettings as Record<string, unknown> | undefined;
  const fpFloors = fraudPointFloors(lnSettings);
  const showRiskIndex = showVerificationRiskIndex(lnSettings);
  // LN mode toggle (Bill 2026-10-06): officers in identity.verify.bridgeCompareOfficers may run the
  // LN-Bridge web report instead of the API to cross-reference what the API returned.
  const bridgeCompareAllowed = (() => {
    const csv = lnSettings?.['identity.verify.bridgeCompareOfficers'];
    const code = (user?.officerCode ?? '').trim().toLowerCase();
    if (typeof csv !== 'string' || !code) return false;
    return csv.split(',').map((x) => x.trim().toLowerCase()).some((x) => x === '*' || x === code);
  })();
  const [lnSource, setLnSource] = useState<'api' | 'bridge'>('api');

  // Bill 2026-09-17: date of birth and phone are required before a LexisNexis
  // Run; a missing email asks the officer to confirm (identity.verify.* settings).
  const lnInputsMissing = missingLnVerifyInputs(person, readLnVerifyInputSettings(config.systemSettings));
  const [warnPromptOpen, setWarnPromptOpen] = useState<boolean>(false);
  const showWarnPrompt = warnPromptOpen && lnInputsMissing.required.length === 0 && lnInputsMissing.warn.length > 0;

  // BACKLOG-0501 — resolve the LN Verification Rule for this Person + cart.
  // Mode drives the run-button visibility / required-state styling.
  // The rules fetch is cached module-scope so multiple PersonModal opens
  // in a session share the result.
  const lnRules = useLnVerificationRules();
  const { citizenship, tenure } = deriveLnAxesFromPerson(person);
  const lnResolution = resolveLnRule(lnRules.rules, {
    cohort: 'Personal',
    citizenship,
    tenure,
    channel: cartChannel,
  });

  const tier = classifyScore(person.amlInsightScore, person.state);
  // Bill 2026-06-12 — the Moderate band's collect-rule is now a LIVE checklist
  // computed from the person's current captured state (photo ID / address
  // corroboration / SSN card), not static text. Null for Low/High bands.
  // Re-derives every render (pure), so capturing a doc flips a row green and
  // the summary line + Verify rollup react immediately.
  const followUp = deriveFraudPointFollowUp(person);
  const todayIso = new Date().toISOString().slice(0, 10);

  // ── Report-pull flow state ────────────────────────────────────────────
  // (Bill 2026-05-18, BACKLOG-0068; one-button rework Bill 2026-06-11.)
  // Holds in-flight status + parsed summary returned from
  // /v3/identity/import-lexisnexis-html (or the direct-connection extract).
  // The inquiry ID is persisted on the person so cart reload preserves the
  // auditable link from RaoPerson → IdentityVerificationInquiry.
  const [flowStatus, setFlowStatus] = useState<'idle' | 'verifying' | 'opened' | 'importing' | 'done' | 'error'>('idle');
  const [flowError, setFlowError] = useState<string | null>(null);
  // Why the direct LexisNexis connection was NOT used this time (server
  // fallbackReason). Bill 2026-09-11: the FSR must see this, not guess.
  const [flowNote, setFlowNote] = useState<string | null>(null);
  const [importHtml, setImportHtml] = useState<string>('');
  const [lastSummary, setLastSummary] = useState<ImportLexisNexisHtmlSummary | null>(null);
  const [showExtractModal, setShowExtractModal] = useState<boolean>(false);

  // The flow step starts collapsed on an existing customer whose person row
  // carries a stale pending inquiry from a prior cart (BACKLOG-0513 — don't
  // shove the import step at the FSR on modal mount); a fresh button click
  // always expands it.
  const [flowCollapsed, setFlowCollapsed] = useState<boolean>(() => isExistingCustomer(person));

  // alpha.98 / BACKLOG-0508 — post-run poll for the LN Bridge extension's
  // import. The extension POSTs to /v3/identity/extension/result server-side;
  // the PersonModal can't observe that directly. We poll the inquiry every 5s
  // for up to 20 min and patch the person row the moment latestExtract
  // appears. Manual "Check now" button calls the same poller.
  const [pollTick, setPollTick] = useState<number>(0);  // bump to force-fetch
  const [pollMessage, setPollMessage] = useState<string | null>(null);
  const pollStopRef = useRef<boolean>(false);
  // BACKLOG-0513 amendment (2026-07-07) — the existing-customer guard is
  // meant to stop the poll AUTO-firing on modal mount; it must NOT suppress
  // the poll for an inquiry the FSR explicitly started (Run button) or
  // explicitly asked about (Check now). Before this flag, an FSR could run
  // LN on an existing customer, the bridge imported the report server-side,
  // and the app never fetched it — "Checking…" hung forever with zero HTTP.
  const manualIntentRef = useRef<boolean>(false);

  // Map RaoPerson → SubjectIdentity for the verify call. Fields that the
  // person hasn't filled in yet stay undefined; LnHtmlParser will tolerate
  // partial fills in the returned report too.
  const buildSubject = (): SubjectIdentity => {
    const ssnDigits = (person.taxId ?? '').replace(/\D/g, '');
    const ssn = ssnDigits.length === 9
      ? `${ssnDigits.slice(0, 3)}-${ssnDigits.slice(3, 5)}-${ssnDigits.slice(5)}`
      : (person.taxId ?? undefined);
    return {
      firstName: person.firstName,
      lastName: person.lastName,
      middleInitial: person.middleName?.[0],
      suffix: person.suffix,
      ssn,
      dob: person.dob, // YYYY-MM-DD in RaoPerson; backend splits for LN
      street: person.addr,
      city: person.city,
      state: person.state,
      zip: person.zip,
      // Prefer the most-likely-current contact number (cell → home → biz).
      // `||`, not `??`: a cleared field holds '' and must fall through.
      phone: person.phoneCell || person.phoneHome || person.phoneBus || undefined,
      email: person.email,
      // ipAddress: only meaningful for RCP online channel; FSR session lacks
      // a customer IP. Leave undefined.
      // Driver license # / state are captured on the document-scan side,
      // not on RaoPerson directly — FSR can manually enter into LN if needed.
    };
  };

  // BACKLOG-0513 — reduced PDW path for existing customers. When the FSR
  // opens a pre-existing CIF on a new cart, LN MUST NOT auto-fire on tab
  // mount. The "LexisNexis AML Insights" button stays visible — it's the
  // only way to trigger a fresh LN run.
  const existingCustomer = isExistingCustomer(person);

  // alpha.98 / BACKLOG-0508 — poll for the extension/extension-result import.
  // Activates when (a) we have an inquiryId and (b) we don't yet have an
  // extractId on the person row. Polls every 5 seconds for up to 20 min.
  // Cancels itself the moment latestExtract appears.
  //
  // BACKLOG-0513 — guarded with `!existingCustomer` so a stale inquiryId
  // carried in from a prior cart doesn't trigger an auto-poll on an
  // existing customer's modal mount. Manual "Check now" still works
  // because it bumps `pollTick`, which the effect's dep list includes —
  // but only after the FSR explicitly clicks LexisNexis AML Insights (which
  // doesn't gate on existingCustomer; the FSR can re-run if they want).
  useEffect(() => {
    // Suppress the MOUNT-time auto-poll for existing customers (BACKLOG-0513),
    // but honor explicit FSR intent: a Run click or a Check-now click sets
    // manualIntentRef and the poll must proceed regardless of customer mode.
    if (existingCustomer && !manualIntentRef.current) return;
    const inquiryId = person.amlInsightInquiryId;
    if (!inquiryId) return;
    if (person.amlInsightExtractId) return;  // already imported — nothing to poll for

    pollStopRef.current = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const startedAt = Date.now();
    const MAX_DURATION_MS = 20 * 60 * 1000;
    const INTERVAL_MS = 5000;

    const tick = async (): Promise<void> => {
      if (pollStopRef.current) return;
      try {
        const r = await api.retail.identity.getInquiry(inquiryId);
        if (r.latestExtract) {
          const x = r.latestExtract.extract;
          const fp = x.fraudPointScore ?? null;
          // FraudPoint Score is the CANONICAL decisioning value (per Bill
          // 2026-05-20 score-conflation correction). Do NOT use
          // verificationRiskIndex — that's the 0–50 ID-element-match band,
          // not the legacy-procedure decisioning threshold.
          // (Bill 2026-06-03) — same Verify auto-clear as the manual-import
          // path: map the report's verification-detail rows into the Verify
          // clearance map so address/phone/email show real status, not "unrun".
          const derivedClearance = deriveLnContactClearance(
            r.latestExtract.verifDetails ?? [],
            x.id,
            { nap: x.verifSummary_NameAddrPhone ?? null, nas: x.verifSummary_NameAddrSsn ?? null },
          );
          const prevClearance = ((person.extra as { verifyClearance?: VerifyClearanceMap } | undefined)
            ?.verifyClearance) ?? {};
          onChange({
            amlInsightExtractId: x.id,
            amlInsightReportDate: todayIso,
            amlInsightRunBy:
              user?.officerCode ?? user?.upn ?? person.amlInsightRunBy ?? '',
            ...(typeof fp === 'number' ? { amlInsightScore: fp, amlInsightScoreSource: 'report' as const } : {}),
            ...(Object.keys(derivedClearance).length > 0
              ? {
                  extra: {
                    ...(person.extra ?? {}),
                    verifyClearance: { ...prevClearance, ...derivedClearance },
                  } as RaoPerson['extra'],
                }
              : {}),
          });
          setLastSummary({
            riskIndex: x.verificationRiskIndex ?? null,
            riskLevelText: x.verificationRiskLevelText ?? null,
            fraudPointScore: fp,
            hasRedFlagSection: x.hasRedFlagSection ?? false,
            redFlagCount: r.latestExtract.redFlags.length,
            fraudWarningCount: r.latestExtract.fraudWarnings.length,
            verificationDetailCount: r.latestExtract.verifDetails.length,
            lexIdFound: x.lexIdFound ?? null,
            normalizedResult: fp == null
              ? 'Inconclusive'
              : fp >= fpFloors.low ? 'Pass' : fp >= fpFloors.moderate ? 'Warn' : 'Fail',
          });
          setPollMessage('Imported.');
          setFlowStatus('done');
          pollStopRef.current = true;
          return;
        }
        if (Date.now() - startedAt > MAX_DURATION_MS) {
          setPollMessage('No import detected after 20 min. Paste the exported report below, or click "Check now" to retry.');
          pollStopRef.current = true;
          return;
        }
        timer = setTimeout(tick, INTERVAL_MS);
      } catch (err) {
        // Transient network errors — surface but keep polling.
        // eslint-disable-next-line no-console
        console.warn('[IndividualAmlInsightSection] getInquiry poll failed', err);
        timer = setTimeout(tick, INTERVAL_MS);
      }
    };

    // Kick off immediately on inquiryId-set / Check-now click; subsequent
    // ticks self-schedule via setTimeout.
    void tick();

    return () => {
      pollStopRef.current = true;
      if (timer) clearTimeout(timer);
    };
    // pollTick is the manual-refresh trigger; re-run when it bumps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person.amlInsightInquiryId, person.amlInsightExtractId, pollTick, existingCustomer]);

  const handleCheckNow = (): void => {
    manualIntentRef.current = true;
    setPollMessage('Checking…');
    setPollTick((t) => t + 1);
  };

  // Open (or re-focus) the LexisNexis window. Sized popup with a named
  // target so re-clicks during the same officer session reuse the existing
  // window (officer's LN sign-in persists; doesn't spawn a new tab per
  // click). LN blocks iframe embedding via X-Frame-Options, so a true
  // in-app surface isn't possible. window.open routes through Teams'
  // external-link handler when running in Teams desktop.
  const openLnPopup = (): void => {
    const features = [
      'width=1280', 'height=860', 'resizable=yes', 'scrollbars=yes',
      'status=yes', 'location=yes', 'toolbar=no', 'menubar=no',
      'noopener=no', 'noreferrer=no',
    ].join(',');
    // Admin-settable so the launch host can be flipped without a redeploy,
    // once the LN Bridge extension update has reached every pilot device.
    const amlUrl =
      String(config.systemSettings?.[AML_INSIGHT_URL_SETTING] ?? '').trim() ||
      LEXISNEXIS_AML_INSIGHT_URL_FALLBACK;
    window.open(amlUrl, 'lexisnexis-aml-popup', features);
  };

  // Direct-connection (SourceMode 'ddp-api') completion. The server already
  // persisted the IdentityProviderExtract row inline; pull it to populate
  // the FraudPoint Score and auto-clear address/phone/email on the Verify
  // tab (Bill 2026-06-03) — no popup, no paste step.
  const applyDirectExtract = async (inquiryId: string, extractId: string): Promise<void> => {
    const patch: Partial<RaoPerson> = {
      amlInsightInquiryId: inquiryId,
      amlInsightExtractId: extractId,
      amlInsightReportDate: todayIso,
      amlInsightRunBy: user?.officerCode ?? user?.upn ?? person.amlInsightRunBy ?? '',
    };
    try {
      const ex = await api.retail.identity.getExtract(extractId);
      const x = ex.extract;
      const fp = x.fraudPointScore ?? null;
      // FraudPoint Score is the CANONICAL decisioning value — NOT the
      // verificationRiskIndex (per Bill 2026-05-20 score-conflation
      // correction).
      if (typeof fp === 'number') {
        patch.amlInsightScore = fp;
        patch.amlInsightScoreSource = 'report';
      }
      const derived = deriveLnContactClearance(ex.verifDetails ?? [], extractId,
        { nap: ex.extract.verifSummary_NameAddrPhone ?? null, nas: ex.extract.verifSummary_NameAddrSsn ?? null });
      if (Object.keys(derived).length > 0) {
        const prevClearance = ((person.extra as { verifyClearance?: VerifyClearanceMap } | undefined)
          ?.verifyClearance) ?? {};
        patch.extra = {
          ...(person.extra ?? {}),
          verifyClearance: { ...prevClearance, ...derived },
        } as RaoPerson['extra'];
      }
      setLastSummary({
        riskIndex: x.verificationRiskIndex ?? null,
        riskLevelText: x.verificationRiskLevelText ?? null,
        fraudPointScore: fp,
        hasRedFlagSection: x.hasRedFlagSection ?? false,
        redFlagCount: ex.redFlags.length,
        fraudWarningCount: ex.fraudWarnings.length,
        verificationDetailCount: ex.verifDetails.length,
        lexIdFound: x.lexIdFound ?? null,
        normalizedResult: fp == null
          ? 'Inconclusive'
          : fp >= fpFloors.low ? 'Pass' : fp >= fpFloors.moderate ? 'Warn' : 'Fail',
      });
    } catch (exErr) {
      // Non-fatal — the report ran and the extract link is recorded (the
      // commit gate sees it); the score just didn't auto-populate. The FSR
      // can open View full report or enter the score manually.
      // eslint-disable-next-line no-console
      console.warn('[IndividualAmlInsightSection] getExtract after direct pull failed', exErr);
    }
    onChange(patch);
    setFlowStatus('done');
  };

  // ── The one primary action: "LexisNexis AML Insights" ────────────────
  // Single call to /v3/identity/verify; the server dispatches API-first
  // (BACKLOG-0518): the direct LexisNexis connection (DdpApiAdapter) runs
  // when configured, and the response's sourceMode tells us which path
  // executed. 'ddp-api' = report already pulled — populate and finish.
  // 'html-import' = website-pull backup — copy the autofill payload, open
  // the LexisNexis window, and surface the in-flow import step
  // (Bill 2026-06-11: this is HOW reports are pulled until the LN API key
  // lands, and the automatic backup whenever the API is unavailable/down).
  const handleRunAmlInsights = async (): Promise<void> => {
    manualIntentRef.current = true; // explicit Run — poll may proceed even for existing customers
    setFlowStatus('verifying');
    setFlowError(null);
    setFlowNote(null);
    setFlowCollapsed(false);
    try {
      const subject = buildSubject();
      const outcome = (await api.retail.identity.verify({
        cartId: undefined, // RaoPerson is cart-scoped via parent; left to backend audit
        // Decides which LexisNexis institution the query is billed and
        // recorded against. Omitting it made the server default to
        // Production, so a Test-Live cart hit the production org.
        ...(cartMode !== undefined ? { cartMode } : {}),
        ...(lnSource === 'bridge' ? { preferredSourceMode: 'html-import' as const } : {}),
        subject,
      })) as VerifyOutcomeWire;

      // Persist the inquiryId on the person so a later session continuation
      // can resume the import flow.
      onChange({ amlInsightInquiryId: outcome.inquiryId });

      if (outcome.sourceMode === 'ddp-api' && outcome.extractId) {
        await applyDirectExtract(outcome.inquiryId, outcome.extractId);
        return;
      }

      // Website-pull (autofill) path. Copy payload to clipboard for the
      // LN Bridge extension to read.
      const payloadText = JSON.stringify(outcome.clipboardPayload ?? {}, null, 2);
      try {
        await navigator.clipboard.writeText(payloadText);
      } catch (e) {
        // Clipboard write can fail if the user hasn't granted permission. We
        // still open LN; the FSR can fall back to the manual copy grid
        // inside the import step.
        // eslint-disable-next-line no-console
        console.warn('[IndividualAmlInsightSection] clipboard.writeText failed', e);
      }

      if (outcome.fallbackReason) {
        const r = outcome.fallbackReason;
        // Say what to DO, matched to what LN actually objected to (Peter's
        // 2026-09-11 retry showed the SSN/DOB hint on a policy problem).
        const hint = /national_id_number|account_date_of_birth/i.test(r)
          ? 'Check the SSN and date of birth on this person and run again.'
          : /no_policy_configured|: policy\b/i.test(r)
            ? 'This is a LexisNexis account setting, not something on this person: an administrator sets the published policy name (Admin Panel > identity.ddp.policyName.prod).'
            : 'Report it from the Debug icon if it keeps happening.';
        setFlowNote(`Direct LexisNexis connection was not used: ${r}. Continuing with the website pull. ${hint}`);
      }

      openLnPopup();
      setFlowStatus('opened');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[IndividualAmlInsightSection] /v3/identity/verify failed', err);

      // THE INQUIRY SURVIVES THE FAILURE. The server creates the
      // IdentityVerificationInquiry row BEFORE dispatching to an adapter, and
      // returns its id even on the 422 failure path (IdentityFunction.cs). We
      // used to drop it here, so a hard failure left the FSR with no inquiry to
      // attach a manual report to — "Click Run first" on a Run that HAD run,
      // with no way forward but abandoning the person. Recovering it keeps the
      // manual html-import path open, which is the whole point of the backup.
      const details = (err as { details?: unknown })?.details;
      const recoveredId =
        details && typeof details === 'object' && typeof (details as { inquiryId?: unknown }).inquiryId === 'string'
          ? (details as { inquiryId: string }).inquiryId
          : undefined;
      if (recoveredId) {
        onChange({ amlInsightInquiryId: recoveredId });
      }

      setFlowStatus('error');
      setFlowError(
        ((err as Error)?.message ?? 'Failed to start the LexisNexis pull.')
        + (recoveredId
            ? ' You can still pull the report from the LexisNexis site and paste it into the import step below.'
            : ''),
      );
    }
  };

  const handleRunClick = (): void => {
    if (lnInputsMissing.required.length > 0) return;
    if (lnInputsMissing.warn.length > 0 && !warnPromptOpen) {
      setWarnPromptOpen(true);
      return;
    }
    setWarnPromptOpen(false);
    void handleRunAmlInsights();
  };

  const handleImportHtml = async (): Promise<void> => {
    const inquiryId = person.amlInsightInquiryId;
    if (!inquiryId) {
      setFlowError('Click "Run" first so we have an inquiry to attach this report to.');
      return;
    }
    if (importHtml.trim().length < 100) {
      setFlowError('HTML body looks too short — paste the full LN report export.');
      return;
    }
    setFlowStatus('importing');
    setFlowError(null);
    try {
      const outcome = await api.retail.identity.importLexisNexisHtml({
        inquiryId,
        html: importHtml,
      });
      setLastSummary(outcome.summary);
      // Auto-populate the manual entry fields from the parsed result.
      const patch: Partial<RaoPerson> = {
        amlInsightExtractId: outcome.extractId,
        amlInsightReportDate: todayIso,
        amlInsightRunBy: user?.officerCode ?? user?.upn ?? person.amlInsightRunBy ?? '',
      };
      if (typeof outcome.summary.fraudPointScore === 'number') {
        patch.amlInsightScore = outcome.summary.fraudPointScore;
        patch.amlInsightScoreSource = 'report';
      }
      // (Bill 2026-06-03) — propagate the LN report's verification of
      // address / phone / email into the Verify tab. The import outcome only
      // carries the summary, so fetch the full extract to read its
      // verification-detail rows, then map matched → pass into
      // person.extra.verifyClearance. Without this the Verify tab stayed
      // "Address: unrun / Phone: unrun" even though the LN report confirmed
      // them, and the CIF could never resolve (the Widener smoke test).
      try {
        const ex = await api.retail.identity.getExtract(outcome.extractId);
        const derived = deriveLnContactClearance(ex.verifDetails ?? [], outcome.extractId,
          { nap: ex.extract.verifSummary_NameAddrPhone ?? null, nas: ex.extract.verifSummary_NameAddrSsn ?? null });
        if (Object.keys(derived).length > 0) {
          const prevClearance = ((person.extra as { verifyClearance?: VerifyClearanceMap } | undefined)
            ?.verifyClearance) ?? {};
          patch.extra = {
            ...(person.extra ?? {}),
            verifyClearance: { ...prevClearance, ...derived },
          } as RaoPerson['extra'];
        }
      } catch (exErr) {
        // Non-fatal — the score still imported; the Verify auto-clear just
        // won't happen (FSR can still clear manually). Log for diagnosis.
        // eslint-disable-next-line no-console
        console.warn('[IndividualAmlInsightSection] getExtract for verify-clearance failed', exErr);
      }
      onChange(patch);
      setFlowStatus('done');
      // Clear the textarea so it doesn't re-submit on a double click.
      setImportHtml('');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[IndividualAmlInsightSection] /v3/identity/import-lexisnexis-html failed', err);
      setFlowStatus('error');
      setFlowError((err as Error)?.message ?? 'Failed to import LN HTML report.');
    }
  };

  // CHG-0050 — a score a LexisNexis report returned is not typed over; the box is the
  // backup for when LexisNexis cannot be reached, and a typed score is marked as such.
  const scoreFromReport = person.amlInsightScoreSource === 'report' && typeof person.amlInsightScore === 'number';
  const handleScoreChange = (raw: string): void => {
    if (scoreFromReport) return;
    if (raw === '') {
      onChange({ amlInsightScore: undefined, amlInsightScoreSource: undefined });
      return;
    }
    const n = parseInt(raw, 10);
    if (Number.isFinite(n)) {
      onChange({ amlInsightScore: Math.max(0, Math.min(997, n)), amlInsightScoreSource: 'manual' });
    }
  };

  const stamp = (): void => {
    onChange({
      amlInsightReportDate: todayIso,
      amlInsightRunBy: user?.officerCode ?? user?.upn ?? '',
    });
  };

  // PROHIBIT — hide the section entirely. Returning null also disables the
  // manual score entry below, so an FSR can't sneak around the rule.
  // The matrix LegalCitation (e.g. "no LN report for minors") is the audit
  // trail; the suppression itself is recorded in cart-commit when the gate
  // checks for an extract on PROHIBIT cohorts (it doesn't — short-circuits).
  if (lnResolution.mode === 'PROHIBIT') return null;

  // The import step renders as a STEP OF THE FLOW (locked card v6): while a
  // pull is active, or whenever an inquiry is still awaiting its report —
  // never as a standing surface at rest.
  const awaitingReport = !!person.amlInsightInquiryId && !person.amlInsightExtractId;
  const flowVisible =
    flowStatus === 'verifying' ||
    flowStatus === 'opened' ||
    flowStatus === 'importing' ||
    flowStatus === 'error' ||
    awaitingReport;

  return (
    <section
      style={{
        margin: 'var(--sp-4) 0',
        padding: 'var(--sp-4)',
        border: '1px solid var(--ink-200, var(--surface-border))',
        borderRadius: 6,
        background: 'var(--surface-1, #fff)',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 'var(--sp-3)' }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
          Identity Verification (Fraud Screening)
        </h3>
        {tier && (
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 4,
              background: tier.bg,
              color: tier.fg,
              border: `1px solid ${tier.border}`,
            }}
          >
            {tier.label}
          </span>
        )}
        {/* CHG-0050 — say where the score came from. This was an "edited" flag that also
            lit up when a LexisNexis run filled the score in. */}
        {typeof person.amlInsightScore === 'number' && person.amlInsightScoreSource && (
          <span
            style={{
              fontSize: 11,
              color: person.amlInsightScoreSource === 'manual' ? 'var(--warn-text, #92400e)' : 'var(--ink-600, #6b7280)',
              fontStyle: 'italic',
            }}
          >
            {person.amlInsightScoreSource === 'report' ? 'from LexisNexis report' : 'entered by hand'}
          </span>
        )}
      </header>

      {/* Card at rest carries NO descriptive prose (smoke test #2, Bill
          2026-06-12) — the FraudPoint band/procedure text renders ONLY in
          the post-run result area (the tier block below). */}

      {/* ── The one primary action (locked card v6, Bill 2026-06-11) ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
        <button
          type="button"
          onClick={handleRunClick}
          disabled={readOnly || flowStatus === 'verifying' || flowStatus === 'importing' || lnInputsMissing.required.length > 0}
          title={lnInputsMissing.required.length > 0
            ? `Enter the customer's ${joinLnInputLabels(lnInputsMissing.required)} first.`
            : lnResolution.mode === 'REQUIRED'
              ? `Required for this customer — ${lnResolution.note ?? lnResolution.legalCitation}`
              : 'Pulls the LexisNexis report. If the direct connection is unavailable, opens LexisNexis with this customer’s details ready to fill in.'}
          style={{
            padding: '8px 14px',
            border: lnResolution.mode === 'REQUIRED'
              ? '2px solid var(--pcb-red, #b10505)'
              : '1px solid var(--pcb-blue, #2968ad)',
            borderRadius: 4,
            background: 'var(--brand-fill)',
            color: 'var(--on-brand)',
            cursor: readOnly || lnInputsMissing.required.length > 0 ? 'not-allowed' : 'pointer',
            fontSize: 13,
            fontWeight: 600,
            opacity: readOnly || lnInputsMissing.required.length > 0 ? 0.5 : 1,
          }}
        >
          Run
          {lnResolution.mode === 'REQUIRED' && (
            <span style={{ marginLeft: 6, color: 'var(--on-brand)' }}>*</span>
          )}
        </button>
        {bridgeCompareAllowed && !readOnly && (
          <label
            style={{ fontSize: 11, color: 'var(--ink-600, #475569)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            title="Run the LexisNexis web report through the LN-Bridge instead of the API, to compare the two. Each run is a separate LexisNexis search."
          >
            via
            <select
              value={lnSource}
              onChange={(e) => setLnSource(e.currentTarget.value === 'bridge' ? 'bridge' : 'api')}
              disabled={flowStatus === 'verifying' || flowStatus === 'importing'}
              style={{ fontSize: 11, padding: '1px 4px', border: '1px solid var(--ink-300, var(--surface-border))', borderRadius: 4, background: 'var(--surface-card, #fff)', color: 'var(--ink-900, #1d1d1f)' }}
              aria-label="LexisNexis source"
            >
              <option value="api">API</option>
              <option value="bridge">LN-Bridge (compare)</option>
            </select>
          </label>
        )}

        {lnResolution.mode === 'REQUIRED' && !person.amlInsightExtractId && (
          <span
            style={{
              fontSize: 11,
              color: 'var(--pcb-red, #b10505)',
              fontWeight: 600,
            }}
            title={lnResolution.note ?? lnResolution.legalCitation}
          >
            REQUIRED for this customer — the account cannot be opened without a LexisNexis report.
          </span>
        )}

        {!readOnly && lnInputsMissing.required.length > 0 && (
          <span style={{ fontSize: 12, color: 'var(--pcb-red, #b10505)', fontWeight: 600 }}>
            Enter the customer's {joinLnInputLabels(lnInputsMissing.required)} to run LexisNexis.
          </span>
        )}
      </div>

      {showWarnPrompt && (
        <div
          role="alert"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            margin: '0 0 var(--sp-3) 0',
            padding: 'var(--sp-2) var(--sp-3)',
            background: 'var(--warn-bg)',
            border: '1px solid var(--warn-line)',
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          <span style={{ flex: '1 1 240px' }}>
            No {joinLnInputLabels(lnInputsMissing.warn)} for this customer. LexisNexis can verify less without it.
            Add it to the customer's contact details, or run without it.
          </span>
          <button
            type="button"
            onClick={handleRunClick}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--pcb-blue)',
              borderRadius: 4,
              background: 'var(--brand-fill)',
              color: 'var(--on-brand)',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Run anyway
          </button>
          <button
            type="button"
            onClick={() => setWarnPromptOpen(false)}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--ink-300, var(--surface-border))',
              borderRadius: 4,
              background: 'var(--surface-card, #fff)',
              color: 'var(--ink-800, #1f2937)',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* ── In-flow import step — only while a pull is active / awaiting ── */}
      {flowVisible && (
        <div
          style={{
            margin: '0 0 var(--sp-3) 0',
            padding: 'var(--sp-3)',
            background: 'var(--surface-2, #f9fafb)',
            border: '1px dashed var(--pcb-blue)',
            borderRadius: 4,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <strong style={{ fontSize: 12, color: 'var(--pcb-blue)' }}>
              {flowStatus === 'verifying'
                ? 'Contacting LexisNexis…'
                : 'Import the LexisNexis report'}
            </strong>
            {person.amlInsightInquiryId && (
              <span style={{ fontSize: 11, color: 'var(--ink-600, #6b7280)', fontFamily: 'monospace' }}>
                Inquiry {person.amlInsightInquiryId.slice(0, 8)}…
              </span>
            )}
            {flowStatus !== 'verifying' && (
              <button
                type="button"
                onClick={() => setFlowCollapsed((v) => !v)}
                style={{
                  marginLeft: 'auto',
                  padding: '4px 10px',
                  border: '1px solid var(--ink-300, var(--surface-border))',
                  borderRadius: 4,
                  background: 'transparent',
                  color: 'var(--ink-700, #4b5563)',
                  cursor: 'pointer',
                  fontSize: 11,
                }}
              >
                {flowCollapsed ? '▼ Show this step' : '▲ Hide this step'}
              </button>
            )}
          </div>

          {!flowCollapsed && flowStatus !== 'verifying' && (
            <>
              <p style={{ margin: 'var(--sp-2) 0 0 0', fontSize: 11, color: 'var(--ink-700, #4b5563)' }}>
                Run the report in the LexisNexis window (re-open it below if needed —
                this customer&rsquo;s details were copied for the autofill). When the report
                is done, export it (Download Report → HTML format) and paste it here —
                the FraudPoint Score and verification results populate automatically.
              </p>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)', alignItems: 'center', marginTop: 'var(--sp-2)' }}>
                <button
                  type="button"
                  onClick={openLnPopup}
                  disabled={readOnly}
                  title="Re-opens the LexisNexis window (your sign-in is kept)"
                  style={{
                    padding: '4px 10px',
                    border: '1px solid var(--pcb-blue)',
                    borderRadius: 4,
                    background: 'transparent',
                    color: 'var(--pcb-blue)',
                    cursor: readOnly ? 'not-allowed' : 'pointer',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  ↗ Re-open LexisNexis
                </button>

                {/* alpha.98 / BACKLOG-0508 — manual Check-now button so the FSR
                    can force a poll cycle if they completed the LN flow and want
                    the result NOW rather than waiting for the 5s tick. */}
                {awaitingReport && (
                  <button
                    type="button"
                    onClick={handleCheckNow}
                    disabled={readOnly}
                    style={{
                      padding: '4px 10px',
                      border: '1px solid var(--ink-300, var(--surface-border))',
                      borderRadius: 4,
                      background: 'transparent',
                      color: 'var(--ink-700, #4b5563)',
                      cursor: readOnly ? 'not-allowed' : 'pointer',
                      fontSize: 11,
                    }}
                    title="Check for an imported LN report now (checks every 5s automatically too)"
                  >
                    ⟳ Check now
                  </button>
                )}

                {pollMessage && !person.amlInsightExtractId && (
                  <span style={{ fontSize: 11, color: 'var(--ink-600, #6b7280)', fontStyle: 'italic' }}>
                    {pollMessage}
                  </span>
                )}
              </div>

              {/* Prefill copy grid (manual fallback) — lives INSIDE the
                  flow step (locked card v6), accessible while the pull is
                  active, never on the card at rest. */}
              <PreFillPanel person={person} />

              <div style={{ marginTop: 'var(--sp-3)' }}>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--ink-700, #4b5563)', marginBottom: 4 }}>
                  Paste the exported LN report below (Download Report → HTML format inside LN):
                </label>
                <textarea
                  value={importHtml}
                  onChange={(e) => setImportHtml(e.target.value)}
                  disabled={readOnly || flowStatus === 'importing'}
                  placeholder="<html>...</html>"
                  spellCheck={false}
                  style={{
                    width: '100%',
                    minHeight: 80,
                    padding: '6px 8px',
                    border: '1px solid var(--ink-300, var(--surface-border))',
                    borderRadius: 4,
                    fontFamily: 'monospace',
                    fontSize: 11,
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
                <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', marginTop: 'var(--sp-2)' }}>
                  <button
                    type="button"
                    onClick={handleImportHtml}
                    disabled={readOnly || flowStatus === 'importing' || importHtml.trim().length < 100}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid var(--pcb-blue)',
                      borderRadius: 4,
                      background: 'var(--brand-fill)',
                      color: 'var(--on-brand)',
                      cursor: readOnly ? 'not-allowed' : 'pointer',
                      fontSize: 12,
                      fontWeight: 600,
                      opacity: (readOnly || flowStatus === 'importing' || importHtml.trim().length < 100) ? 0.5 : 1,
                    }}
                  >
                    {flowStatus === 'importing' ? 'Parsing…' : 'Import & populate score'}
                  </button>
                  <span style={{ fontSize: 11, color: 'var(--ink-600, #6b7280)' }}>
                    {importHtml.trim().length > 0 ? `${importHtml.length.toLocaleString()} chars` : ''}
                  </span>
                </div>
              </div>
            </>
          )}

          {flowError && (
            <div style={{
              marginTop: 'var(--sp-2)',
              padding: '6px 10px',
              background: 'var(--err-bg)',
              color: 'var(--err-text)',
              border: '1px solid var(--err-fill)',
              borderRadius: 4,
              fontSize: 11,
            }}>
              {flowError}
            </div>
          )}

          {flowNote && (
            <div
              role="status"
              style={{
                marginTop: 'var(--sp-2)',
                padding: '6px 10px',
                background: 'rgba(133, 100, 4, 0.10)',
                color: 'var(--pcb-amber-text, #6b5200)',
                border: '1px solid rgba(133, 100, 4, 0.45)',
                borderRadius: 4,
                fontSize: 11,
                lineHeight: 1.4,
              }}
            >
              {flowNote}
            </div>
          )}
        </div>
      )}

      {lastSummary && flowStatus === 'done' && (
        <div style={{
          margin: '0 0 var(--sp-3) 0',
          padding: '6px 10px',
          background: 'var(--ok-bg)',
          color: 'var(--ok-text)',
          border: '1px solid var(--ok-fill)',
          borderRadius: 4,
          fontSize: 11,
        }}>
          ✓ Imported. {showRiskIndex ? `Risk index ${lastSummary.riskIndex ?? '—'} · ` : ''}
          FraudPoint {lastSummary.fraudPointScore ?? '—'} ·
          Red flags {lastSummary.redFlagCount} ·
          Fraud warnings {lastSummary.fraudWarningCount} ·
          Verdict {lastSummary.normalizedResult}
        </div>
      )}

      {/* Risk Management's decision on this LexisNexis result (Risk panel > Verifications > Reviews, Bill 2026-11-02). */}
      {(() => {
        const d = (person as unknown as { extra?: { verificationRiskDecision?: { decision?: string; extractId?: string | null; by?: string } } })
          .extra?.verificationRiskDecision;
        if (!d || (d.decision !== 'cleared' && d.decision !== 'declined')) return null;
        if (d.extractId && person.amlInsightExtractId && d.extractId !== person.amlInsightExtractId) return null; // a newer report
        const cleared = d.decision === 'cleared';
        return (
          <div
            role="status"
            style={{
              margin: '0 0 var(--sp-3) 0',
              padding: '8px 12px',
              borderRadius: 4,
              fontSize: 12,
              border: `1px solid ${cleared ? 'var(--pcb-green, #386732)' : 'var(--pcb-red, #b10505)'}`,
              background: cleared ? 'var(--banner-ok-bg, #eef6ec)' : 'var(--banner-err-bg, #fdecec)',
            }}
          >
            {cleared
              ? `Risk Management cleared this LexisNexis result${d.by ? ` (${d.by})` : ''}. The application can continue.`
              : `Risk Management declined this applicant${d.by ? ` (${d.by})` : ''}. Do not open the account.`}
          </div>
        );
      })()}

      {person.amlInsightExtractId && (
        <div style={{ margin: '0 0 var(--sp-3) 0' }}>
          <button
            type="button"
            onClick={() => setShowExtractModal(true)}
            title="Open the full ~80-field parsed report (verification detail, red flags, fraud warnings, watchlist, IP geolocation, etc.)"
            style={{
              padding: '4px 12px',
              border: '1px solid var(--pcb-blue)',
              borderRadius: 4,
              background: 'transparent',
              color: 'var(--pcb-blue)',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            📋 View full report
          </button>
          <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--ink-600, #6b7280)', fontFamily: 'monospace' }}>
            Extract {person.amlInsightExtractId.slice(0, 8)}…
          </span>
        </div>
      )}

      <IdentityExtractDetailModal
        open={showExtractModal}
        extractId={person.amlInsightExtractId}
        onClose={() => setShowExtractModal(false)}
        channel={cartChannel}
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-3)', alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
          <span style={{ fontWeight: 500, color: 'var(--ink-800, #1f2937)' }}>
            Fraud Point Score (0-997)
          </span>
          <input
            type="number"
            min={0}
            max={997}
            step={1}
            value={person.amlInsightScore ?? ''}
            onChange={(e) => handleScoreChange(e.target.value)}
            onBlur={(e) => {
              if (!scoreFromReport && e.target.value !== '') stamp();
            }}
            disabled={readOnly || scoreFromReport}
            title={scoreFromReport ? 'Set by the LexisNexis report. Run the report again to change it.' : undefined}
            placeholder="e.g. 820"
            style={{
              width: 120,
              padding: '6px 8px',
              border: '1px solid var(--ink-300, var(--surface-border))',
              borderRadius: 4,
              fontSize: 13,
            }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
          <span style={{ fontWeight: 500, color: 'var(--ink-800, #1f2937)' }}>Report Date</span>
          <input
            type="date"
            value={person.amlInsightReportDate ?? ''}
            onChange={(e) => onChange({ amlInsightReportDate: e.target.value })}
            disabled={readOnly}
            style={{
              padding: '6px 8px',
              border: '1px solid var(--ink-300, var(--surface-border))',
              borderRadius: 4,
              fontSize: 13,
            }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
          <span style={{ fontWeight: 500, color: 'var(--ink-800, #1f2937)' }}>Run by</span>
          <input
            type="text"
            value={person.amlInsightRunBy ?? ''}
            onChange={(e) => onChange({ amlInsightRunBy: e.target.value })}
            disabled={readOnly}
            placeholder={user?.officerCode ?? '(officer code)'}
            style={{
              width: 120,
              padding: '6px 8px',
              border: '1px solid var(--ink-300, var(--surface-border))',
              borderRadius: 4,
              fontSize: 13,
            }}
          />
        </label>
      </div>

      {tier && followUp ? (
        /* Moderate band (500–749) — reactive follow-up checklist. Each required
           action reflects whether it's already satisfied by the captured state;
           the verbatim procedure language is preserved as the per-row "why"
           tooltip + the band rule below the rows. (Bill 2026-06-12.) */
        <ModerateFollowUpChecklist tier={tier} followUp={followUp} personId={person.id} />
      ) : tier ? (
        <div
          style={{
            marginTop: 'var(--sp-3)',
            padding: 'var(--sp-2) var(--sp-3)',
            background: tier.bg,
            color: tier.fg,
            border: `1px solid ${tier.border}`,
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          <strong>{tier.label}</strong> &mdash; {tier.rule}
        </div>
      ) : null}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ModerateFollowUpChecklist — the reactive Moderate-band (500–749) collect-rule.
//
// Bill 2026-06-12: "the risk language should reflect that the suggested actions
// have been satisfied." Each market-correct required action renders as a row
// with a satisfied (✓ green) or unmet (⚠ amber) state computed live from the
// person's captured state (deriveFraudPointFollowUp). When every REQUIRED row is
// satisfied → green "Follow-up items satisfied — clear to proceed"; otherwise the
// amber "collect these items before continuing; if the customer can't provide
// them, ask a supervisor to review the application" line. The
// verbatim procedure language stays available as each row's "why" tooltip (title)
// + the band rule line — nothing legal is deleted, it's just made reactive.
// ─────────────────────────────────────────────────────────────────────────

function ModerateFollowUpChecklist({
  tier,
  followUp,
  personId,
}: {
  tier: ScoreTier;
  followUp: NonNullable<ReturnType<typeof deriveFraudPointFollowUp>>;
  /** peopleJson person.id — keys the supervisor override (BACKLOG-1787). */
  personId: string | undefined;
}): JSX.Element {
  const allSatisfied = followUp.allRequiredSatisfied;
  return (
    <div
      style={{
        marginTop: 'var(--sp-3)',
        padding: 'var(--sp-2) var(--sp-3)',
        background: tier.bg,
        color: tier.fg,
        border: `1px solid ${tier.border}`,
        borderRadius: 4,
        fontSize: 12,
      }}
    >
      <div style={{ marginBottom: 'var(--sp-2)' }}>
        <strong>{tier.label}</strong> &mdash; follow-up REQUIRED before continuing (
        {followUp.outOfMarket ? 'out-of-market / out-of-state' : 'in-market'}).
      </div>

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {followUp.items.map((item) => (
          <FollowUpRow key={item.key} item={item} />
        ))}
      </ul>

      {allSatisfied ? (
        <div
          style={{
            marginTop: 'var(--sp-2)',
            padding: '6px 10px',
            background: 'var(--ok-bg)',
            color: 'var(--ok-text)',
            border: '1px solid var(--ok-fill)',
            borderRadius: 4,
            fontWeight: 600,
          }}
        >
          ✓ Follow-up items satisfied — clear to proceed.
        </div>
      ) : (
        <div
          style={{
            marginTop: 'var(--sp-2)',
            padding: '6px 10px',
            background: 'var(--warn-bg)',
            color: 'var(--warn-text)',
            border: '1px solid var(--warn-line)',
            borderRadius: 4,
            fontWeight: 600,
          }}
        >
          ⚠ Collect these items before continuing. If the customer can't provide them, ask a supervisor to review the application.
        </div>
      )}

      {/* BACKLOG-1787 — who may override, the override action, and the recorded override. */}
      <SupervisorOverridePanel personId={personId} followUpUnmet={!allSatisfied} />

      <p
        style={{ margin: 'var(--sp-2) 0 0 0', fontSize: 11, opacity: 0.85 }}
        title={followUp.items[0]?.why}
      >
        RDA/ATM not auto-restricted (case-by-case); incoming B2B transfers stay restricted.
      </p>
    </div>
  );
}

function FollowUpRow({ item }: { item: FollowUpItem }): JSX.Element {
  const satisfied = item.satisfied;
  const conditionalUnmet = !satisfied && item.requirement === 'conditional';
  // ✓ green when satisfied; ⚠ amber when a REQUIRED item is unmet; a softer
  // amber/grey for an unmet CONDITIONAL item (only required on a discrepancy).
  const mark = satisfied ? '✓' : '⚠';
  const markColor = satisfied ? 'var(--ok-text)' : conditionalUnmet ? 'var(--warn-text)' : 'var(--err-text)';
  return (
    <li
      style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}
      title={item.why}
    >
      <span
        aria-hidden
        style={{ color: markColor, fontWeight: 700, lineHeight: '1.3', flexShrink: 0 }}
      >
        {mark}
      </span>
      <span style={{ lineHeight: '1.35' }}>
        <span style={{ fontWeight: 600 }}>{item.label}</span>
        {item.requirement === 'conditional' && (
          <span style={{ fontSize: 10, marginLeft: 6, opacity: 0.75, fontStyle: 'italic' }}>
            (conditional)
          </span>
        )}
        {item.detail && (
          <span style={{ display: 'block', fontSize: 11, opacity: 0.85 }}>{item.detail}</span>
        )}
      </span>
    </li>
  );
}

// -----------------------------------------------------------------------------
// PreFillPanel - maps v3 Person fields to LexisNexis InstantID form fields
// per LN's documented format spec (DOB split MM/DD/YYYY, SSN with dashes,
// Phone with-or-without dashes, ZIP 5 or 5-4). Each field has a Copy button
// so the officer can paste into the LN form one field at a time. Plus a
// "Copy all (TSV)" button for the whole record.
//
// Locked card v6 (Bill 2026-06-11): this panel renders INSIDE the in-flow
// import step only — it is no longer a standing expander on the card.
// -----------------------------------------------------------------------------

interface PreFillField {
  key: string;
  lnLabel: string;
  /** LN form element id harvested via DevTools 2026-04-27. */
  lnId: string;
  value: string;
  hint?: string;
}

function buildPreFillFields(p: RaoPerson): PreFillField[] {
  // DOB split: ISO YYYY-MM-DD -> { MM, DD, YYYY }
  const dobParts = (p.dob ?? '').split('-');
  const dobYYYY = dobParts.length >= 1 ? dobParts[0] : '';
  const dobMM = dobParts.length >= 2 ? dobParts[1] : '';
  const dobDD = dobParts.length >= 3 ? dobParts[2] : '';

  // SSN: format as XXX-XX-XXXX when 9 digits.
  // Pre-fix the gate was `tinCode === 'S' && 9 digits`. That dropped the
  // SSN from the prefill payload whenever an applicant was added from an
  // existing-CIF lookup that didn't carry tinCode through, even though
  // taxId held a valid 9-digit number. LN's form takes any 9-digit string
  // as SSN — there's no upside to gating on tinCode here. (For ITIN /
  // EIN / Forn TIN holders, taxDigits will still typically be 9 digits;
  // LN returns "no match" cleanly rather than mis-firing on a non-SSN.)
  const taxDigits = (p.taxId ?? '').replace(/\D/g, '');
  const ssn = taxDigits.length === 9
    ? `${taxDigits.slice(0, 3)}-${taxDigits.slice(3, 5)}-${taxDigits.slice(5)}`
    : taxDigits;

  // Phone: prefer cell, then home, then business (`||` so a cleared '' falls
  // through); format with dashes if 10 digits
  const phoneRaw = (p.phoneCell || p.phoneHome || p.phoneBus || '').replace(/\D/g, '');
  const phone = phoneRaw.length === 10
    ? `${phoneRaw.slice(0, 3)}-${phoneRaw.slice(3, 6)}-${phoneRaw.slice(6)}`
    : phoneRaw;

  // Street: addr + addr2 if present
  const street = [p.addr, p.addr2].filter((s) => s && s.trim().length > 0).join(' ').trim();

  // Gender: v3 stores '' | 'Male' | 'Female'; LN expects 'M' / 'F' selection
  const gender = p.gender === 'Male' ? 'M' : p.gender === 'Female' ? 'F' : '';

  return [
    { key: 'ssn',        lnLabel: 'SSN',                lnId: 'SSN',              value: ssn,                                  hint: '9 digits with-or-without dashes' },
    { key: 'dobMM',      lnLabel: 'DOB:MM',             lnId: 'DOB_M',            value: dobMM ?? '',                          hint: '2-digit month' },
    { key: 'dobDD',      lnLabel: 'DOB:DD',             lnId: 'DOB_D',            value: dobDD ?? '',                          hint: '2-digit day' },
    { key: 'dobYYYY',    lnLabel: 'DOB:YYYY',           lnId: 'DOB_Y',            value: dobYYYY ?? '',                        hint: '4-digit year' },
    { key: 'lastName',   lnLabel: 'Last Name',          lnId: 'LAST_NAME',        value: p.lastName ?? '',                                                            },
    { key: 'firstName',  lnLabel: 'First Name',         lnId: 'FIRST_NAME',       value: p.firstName ?? '',                                                           },
    { key: 'middle',     lnLabel: 'Middle or Initial',  lnId: 'MI',               value: p.middleName ?? '',                                                          },
    { key: 'suffix',     lnLabel: 'Suffix',             lnId: 'NAME_SUFFIX',      value: p.suffix ?? '',                                                              },
    { key: 'street',     lnLabel: 'Street Address',     lnId: 'STREET_ADDRESS',   value: street,                                                                       },
    { key: 'city',       lnLabel: 'City',               lnId: 'CITY',             value: p.city ?? '',                                                                 },
    { key: 'state',      lnLabel: 'State',              lnId: 'STATE',            value: p.state ?? '',                        hint: 'LN <select>; matches by value' },
    { key: 'zip',        lnLabel: 'ZIP Code',           lnId: 'ZIP',              value: p.zip ?? '',                          hint: '5 or 9 digit'                    },
    { key: 'phone',      lnLabel: 'Phone',              lnId: 'PHONE',            value: phone,                                                                        },
    { key: 'email',      lnLabel: 'Email',              lnId: 'EMAIL',            value: p.email ?? '',                                                                },
    { key: 'gender',     lnLabel: 'Gender',             lnId: 'PASSPORT_GENDER',  value: gender,                               hint: 'M / F <select>; matches by value' },
  ];
}


function PreFillPanel({ person }: { person: RaoPerson }): JSX.Element {
  const fields = buildPreFillFields(person);
  const present = fields.filter((f) => f.value && f.value.length > 0);

  const copyOne = async (value: string): Promise<void> => {
    if (!value) return;
    try { await navigator.clipboard.writeText(value); } catch { /* clipboard blocked */ }
  };

  const copyAllTsv = async (): Promise<void> => {
    const tsv = present.map((f) => `${f.lnLabel}\t${f.value}`).join('\n');
    try { await navigator.clipboard.writeText(tsv); } catch { /* clipboard blocked */ }
  };


  return (
    <details
      style={{
        marginTop: 'var(--sp-3)',
        border: '1px solid var(--ink-200, var(--surface-border))',
        borderRadius: 4,
        background: 'var(--surface-2, #fafafa)',
        padding: 'var(--sp-2) var(--sp-3)',
      }}
    >
      <summary
        style={{
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--ink-800, #1f2937)',
          padding: '4px 0',
        }}
      >
        Pre-fill data for LexisNexis ({present.length} of {fields.length} fields populated)
      </summary>
      <div style={{ marginTop: 'var(--sp-2)' }}>
        <p style={{ margin: '0 0 var(--sp-2) 0', fontSize: 11, color: 'var(--ink-700, #4b5563)' }}>
          Open LexisNexis in the popup. The PCB LN Bridge extension fills the form for you — use
          its “Retrieve PII Data” button on the LN page. These rows are the manual fallback if the
          extension is unavailable; values are already formatted per LN’s spec (DOB split, SSN with
          dashes, Phone with dashes when 10 digits).
        </p>
        <div style={{ marginBottom: 'var(--sp-2)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => void copyAllTsv()}
            style={{
              padding: '4px 10px',
              fontSize: 11,
              border: '1px solid var(--ink-400, #9ca3af)',
              borderRadius: 4,
              background: 'var(--surface-card, #fff)',
              color: 'var(--ink-900, #1d1d1f)',
              cursor: 'pointer',
            }}
          >
            Copy all populated fields (TSV)
          </button>
        </div>
        {/* Layout mirrors the LN InstantID Consumer Verification form so the
            Copy button on each cell sits right next to where the FSR is about
            to paste in the LN window. Bill 2026-04-27: the previous tabular
            full-width layout made it hard to visually match a Copy row to its
            target LN field. Grid rows now follow LN's row groupings:
              [ SSN (12) ]
              [ DOB:MM | DOB:DD | DOB:YYYY ]
              [ Last | First | Middle ]
              [ Suffix (12) ]
              [ Street Address (12) ]
              [ City | State | ZIP ]
              [ Phone (6) | Email (6) ]
              [ Gender (12) ] */}
        <LnPrefillGrid fields={fields} onCopy={(v) => void copyOne(v)} />
        <p style={{ margin: 'var(--sp-2) 0 0 0', fontSize: 11, color: 'var(--ink-500, #9ca3af)' }}>
          Not yet captured by ROP: IP Address, Driver License Number / State, Passport upper / lower lines.
          Officer leaves blank or types directly.
        </p>
      </div>
    </details>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// LnPrefillGrid — visual mirror of the LN InstantID form so each Copy
// button sits next to where the FSR will paste in the LN window. Cells are
// keyed by the same `key` as PreFillField so a missing field renders as a
// dim placeholder, preserving spatial alignment with LN's actual layout.
// ─────────────────────────────────────────────────────────────────────────

interface LnPrefillGridProps {
  fields: PreFillField[];
  onCopy: (value: string) => void;
}

function LnPrefillGrid({ fields, onCopy }: LnPrefillGridProps): JSX.Element {
  const byKey = new Map(fields.map((f) => [f.key, f]));
  const cell = (key: string, span: number): JSX.Element =>
    <LnPrefillCell key={key} field={byKey.get(key)} colSpan={span} onCopy={onCopy} />;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        gap: 'var(--sp-2)',
        marginTop: 'var(--sp-2)',
      }}
    >
      {/* Row 1: SSN — full width */}
      {cell('ssn', 12)}
      {/* Row 2: DOB MM | DD | YYYY */}
      {cell('dobMM', 4)}
      {cell('dobDD', 4)}
      {cell('dobYYYY', 4)}
      {/* Row 3: Last | First | Middle */}
      {cell('lastName', 4)}
      {cell('firstName', 4)}
      {cell('middle', 4)}
      {/* Row 4: Suffix */}
      {cell('suffix', 12)}
      {/* Row 5: Street */}
      {cell('street', 12)}
      {/* Row 6: City | State | ZIP */}
      {cell('city', 4)}
      {cell('state', 4)}
      {cell('zip', 4)}
      {/* Row 7: Phone | Email */}
      {cell('phone', 6)}
      {cell('email', 6)}
      {/* Row 8: Gender */}
      {cell('gender', 12)}
    </div>
  );
}

interface LnPrefillCellProps {
  field?: PreFillField;
  colSpan: number;
  onCopy: (value: string) => void;
}

function LnPrefillCell({ field, colSpan, onCopy }: LnPrefillCellProps): JSX.Element {
  const hasValue = !!field?.value;
  return (
    <div
      style={{
        gridColumn: `span ${colSpan}`,
        border: '1px solid var(--ink-200, #e5e7eb)',
        borderRadius: 4,
        padding: 'var(--sp-2)',
        background: hasValue ? 'var(--surface-card)' : 'var(--surface-2, #fafafa)',
        opacity: hasValue ? 1 : 0.55,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minHeight: 56,
      }}
    >
      <div
        style={{
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          color: 'var(--ink-600, #6b7280)',
          fontWeight: 600,
        }}
      >
        {field?.lnLabel ?? '—'}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ fontFamily: 'monospace', fontSize: 12, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {field?.value || <em style={{ color: 'var(--ink-500, #9ca3af)' }}>(blank)</em>}
        </div>
        <button
          type="button"
          disabled={!hasValue}
          onClick={() => field && onCopy(field.value)}
          style={{
            padding: '2px 10px',
            fontSize: 11,
            border: '1px solid var(--ink-300, var(--surface-border))',
            borderRadius: 3,
            background: hasValue ? 'var(--surface-card)' : 'transparent',
            cursor: hasValue ? 'pointer' : 'not-allowed',
            flexShrink: 0,
          }}
        >
          Copy
        </button>
      </div>
      {field?.hint && (
        <div style={{ fontSize: 10, color: 'var(--ink-500, #9ca3af)', fontStyle: 'italic' }}>
          {field.hint}
        </div>
      )}
    </div>
  );
}
